import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import EmptyState from '../components/EmptyState';

function formatarData(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short' });
}

export default function Clientes() {
  const { temPermissao } = useAuth();
  const podeEditar = temPermissao('clientes_editar');
  const [clientes, setClientes] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [carregando, setCarregando] = useState(true);

  const carregar = async () => {
    setCarregando(true);
    try {
      const { data } = await api.get('/clientes');
      setClientes(data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Falha', confirmButtonColor: '#2e9846' });
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const toggleAtivo = async (c) => {
    try {
      await api.put(`/clientes/${c.id}/ativo`, { ativo: !c.ativo });
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Falha', confirmButtonColor: '#2e9846' });
    }
  };

  const editarEmail = async (c) => {
    const { value } = await Swal.fire({
      title: `E-mail de ${c.nome}`,
      input: 'email',
      inputValue: c.email || '',
      inputPlaceholder: 'cliente@email.com',
      showCancelButton: true,
      confirmButtonColor: '#2e9846',
    });
    if (value === undefined) return;
    try {
      await api.put(`/clientes/${c.id}/email`, { email: value || null });
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Falha', confirmButtonColor: '#2e9846' });
    }
  };

  const resetarSenha = async (c) => {
    const r = await Swal.fire({
      icon: 'question',
      title: `Resetar senha de ${c.nome}?`,
      text: c.email
        ? 'Uma nova senha será gerada e enviada por e-mail.'
        : 'O cliente não tem e-mail. A senha será mostrada nesta tela para você passar.',
      showCancelButton: true,
      confirmButtonColor: '#2e9846',
      confirmButtonText: 'Resetar',
    });
    if (!r.isConfirmed) return;
    try {
      const { data } = await api.post(`/clientes/${c.id}/resetar-senha`);
      if (data.enviado_por_email) {
        Swal.fire({ icon: 'success', title: 'Senha enviada', text: 'Cliente recebeu por e-mail.', confirmButtonColor: '#2e9846' });
      } else {
        Swal.fire({
          icon: 'info',
          title: 'Nova senha',
          html: `<div>Repasse manualmente ao cliente:</div>
                 <div style="font-family: monospace; font-size: 1.4rem; margin-top: 10px; color: #2e9846;">${data.senha}</div>`,
          confirmButtonColor: '#2e9846',
        });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Falha', confirmButtonColor: '#2e9846' });
    }
  };

  const desvincular = async (c) => {
    const r = await Swal.fire({
      icon: 'warning',
      title: `Desvincular ${c.nome}?`,
      text: 'O cliente perde o acesso. Para reativar, use o botão "Re-vincular".',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Desvincular',
    });
    if (!r.isConfirmed) return;
    try {
      await api.delete(`/clientes/${c.id}/vinculo`);
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Falha', confirmButtonColor: '#2e9846' });
    }
  };

  const revincular = async (c) => {
    try {
      await api.put(`/clientes/${c.id}/vinculo`);
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.error || 'Falha', confirmButtonColor: '#2e9846' });
    }
  };

  const lista = clientes.filter((c) => {
    if (!filtro) return true;
    const t = filtro.toLowerCase();
    return c.nome?.toLowerCase().includes(t) || c.cgc?.includes(filtro) || c.email?.toLowerCase().includes(t);
  });

  return (
    <>
      <div className="page-header-cli">
        <h1 className="page-title-cli"><i className="bi bi-people-fill"></i> Clientes</h1>
        <input
          className="input-cli"
          placeholder="Buscar por nome / CPF / e-mail…"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          style={{ maxWidth: 320 }}
        />
      </div>

      {carregando ? (
        <div className="card-cli">Carregando…</div>
      ) : lista.length === 0 ? (
        <EmptyState icon="bi-people" titulo="Nenhum cliente vinculado ainda" mensagem="Os clientes aparecem aqui quando fazem o primeiro login." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tabela-cli">
            <thead>
              <tr>
                <th>Nome</th>
                <th>CPF/CNPJ</th>
                <th>E-mail</th>
                <th>Devices</th>
                <th>Status</th>
                <th>Vinculado em</th>
                {podeEditar && <th style={{ width: 280 }}>Ações</th>}
              </tr>
            </thead>
            <tbody>
              {lista.map((c) => (
                <tr key={c.id}>
                  <td>{c.nome}</td>
                  <td style={{ fontFamily: 'monospace' }}>{c.cgc}</td>
                  <td>{c.email || <span style={{ color: 'var(--texto-suave)' }}>—</span>}</td>
                  <td>
                    <span className="tag-cli cinza">
                      <i className="bi bi-phone" style={{ marginRight: 4 }}></i>{c.qtd_devices}
                    </span>
                  </td>
                  <td>
                    {!c.vinculado
                      ? <span className="tag-cli vermelho">Desvinculado</span>
                      : c.ativo
                        ? <span className="tag-cli">Ativo</span>
                        : <span className="tag-cli cinza">Inativo</span>}
                    {c.senha_provisoria ? <span className="tag-cli laranja" style={{ marginLeft: 4 }}>Senha provisória</span> : null}
                  </td>
                  <td>{formatarData(c.criado_em)}</td>
                  {podeEditar && (
                    <td>
                      <div className="acoes-row-cli">
                        <button className="btn-ghost-cli" onClick={() => editarEmail(c)}>
                          <i className="bi bi-envelope"></i> E-mail
                        </button>
                        <button className="btn-ghost-cli" onClick={() => resetarSenha(c)}>
                          <i className="bi bi-key"></i> Reset
                        </button>
                        {c.vinculado ? (
                          <>
                            <button className="btn-ghost-cli" onClick={() => toggleAtivo(c)}>
                              <i className={'bi ' + (c.ativo ? 'bi-pause' : 'bi-play')}></i>
                              {c.ativo ? 'Desativar' : 'Ativar'}
                            </button>
                            <button className="btn-ghost-cli" onClick={() => desvincular(c)}>
                              <i className="bi bi-x-circle"></i> Desvincular
                            </button>
                          </>
                        ) : (
                          <button className="btn-ghost-cli" onClick={() => revincular(c)}>
                            <i className="bi bi-arrow-counterclockwise"></i> Re-vincular
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
